/**
 * Quote List Module
 * Allows users to add products to a quote list and submit quote requests.
 * Integrates with the cart drawer as a tab panel.
 * Supports moving items between cart and quote list.
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'qep_quote_list';

  class QuoteList {
    constructor() {
      this.items = this.load();
      this.currentStep = 1; // Step state management

      // Cart drawer elements (Quote List is now a tab in the cart drawer)
      this.cartDrawer = document.getElementById('CartDrawer');
      this.emptyState = document.getElementById('QuoteListEmpty');
      this.itemsContainer = document.getElementById('QuoteListItems');
      this.itemsList = document.getElementById('QuoteItemsList');
      this.footer = document.getElementById('QuoteListFooter');
      this.itemCount = document.getElementById('QuoteItemCount');
      this.form = document.getElementById('QuoteContactForm');

      // Step containers
      this.step1 = document.getElementById('QuoteStep1');
      this.step2 = document.getElementById('QuoteStep2');

      // Step 2 form fields (inline, not modals)
      this.notesField = document.querySelector('[data-quote-notes]');
      this.nameField = document.querySelector('[data-quote-name]');
      this.emailField = document.querySelector('[data-quote-email]');
      this.phoneField = document.querySelector('[data-quote-phone]');
      this.uploadField = document.querySelector('[data-quote-upload]');
      this.uploadPreview = document.querySelector('[data-quote-upload-preview]');

      // New Step 2 fields
      this.districtField = document.querySelector('[data-quote-district]');
      this.schoolNameField = document.querySelector('[data-quote-school-name]');
      this.bidNumberField = document.querySelector('[data-quote-bid-number]');
      this.streetAddressField = document.querySelector('[data-quote-street-address]');
      this.cityField = document.querySelector('[data-quote-city]');
      this.stateField = document.querySelector('[data-quote-state]');
      this.zipCodeField = document.querySelector('[data-quote-zip-code]');
      // Tax exempt fields (form fields only, no upload)
      this.taxExemptCheckbox = document.querySelector('[data-quote-tax-exempt]');
      this.taxExemptIdField = document.querySelector('[data-quote-tax-exempt-id]');
      this.taxExemptDetailsField = document.querySelector('[data-quote-tax-exempt-details]');

      // Hidden form fields
      this.hiddenName = document.getElementById('QuoteHiddenName');
      this.hiddenEmail = document.getElementById('QuoteHiddenEmail');
      this.hiddenPhone = document.getElementById('QuoteHiddenPhone');
      this.hiddenBody = document.getElementById('QuoteHiddenBody');

      // Uploaded files storage
      this.uploadedFiles = [];

      // Success message element
      this.successMessage = document.getElementById('QuoteSuccessMessage');

      this.init();
    }

    // ========== Storage Methods ==========

    load() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (data) {
          const parsed = JSON.parse(data);
          return parsed.items || [];
        }
      } catch (e) {
        console.error('QuoteList: Failed to load from localStorage', e);
      }
      return [];
    }

    save() {
      try {
        const data = {
          items: this.items,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error('QuoteList: Failed to save to localStorage', e);
      }
    }

    // ========== Item Management ==========

    addItem(productData, options = {}) {
      // Check if item already exists
      const existingIndex = this.items.findIndex(item => item.id === productData.id);

      if (existingIndex >= 0) {
        // Item already in list
        if (!options.silent) {
          this.openQuoteTab();
        }
        return false;
      }

      const item = {
        id: productData.id,
        productId: productData.productId,
        title: productData.title,
        variant: productData.variant || '',
        isbn: productData.isbn || '',
        sku: productData.sku || '',
        author: productData.author || '',
        price: productData.price || '',
        image: productData.image || '',
        url: productData.url || '',
        quantity: productData.quantity || 1,
        addedAt: new Date().toISOString()
      };

      this.items.push(item);
      this.save();
      this.updateContent();

      if (!options.silent) {
        this.openQuoteTab();
      }

      // Dispatch event for other scripts
      document.dispatchEvent(new CustomEvent('quotelist:added', { detail: { item } }));

      return true;
    }

    removeItem(variantId, options = {}) {
      const index = this.items.findIndex(item => item.id === variantId);
      if (index >= 0) {
        const removed = this.items.splice(index, 1)[0];
        this.save();
        this.updateContent();

        document.dispatchEvent(new CustomEvent('quotelist:removed', { detail: { item: removed } }));
        return removed;
      }
      return null;
    }

    getItem(variantId) {
      return this.items.find(item => item.id === variantId);
    }

    clearAll() {
      this.items = [];
      this.save();
      this.updateContent();
      this.goToStep1();

      document.dispatchEvent(new CustomEvent('quotelist:cleared'));
    }

    getItemCount() {
      return this.items.length;
    }

    // ========== Cart API Methods ==========

    async addToCart(variantId, quantity = 1) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        return await response.json();
      } catch (error) {
        console.error('QuoteList: Failed to add to cart', error);
        throw error;
      }
    }

    async removeFromCart(lineKey) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove from cart');
        }

        return await response.json();
      } catch (error) {
        console.error('QuoteList: Failed to remove from cart', error);
        throw error;
      }
    }

    async refreshCartDrawer() {
      // Refresh the cart drawer section to show updated cart
      if (this.cartDrawer) {
        const sectionId = this.cartDrawer.dataset.sectionId;
        if (sectionId) {
          try {
            const response = await fetch(`/?section_id=${sectionId}`);
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newDrawer = doc.getElementById('CartDrawer');
            if (newDrawer) {
              // Preserve the Quote List panel since it's managed by JS
              const quotePanel = this.cartDrawer.querySelector('[id^="QuoteList-"]');
              const newCartPanel = newDrawer.querySelector('[id^="MiniCart-"]');
              const oldCartPanel = this.cartDrawer.querySelector('[id^="MiniCart-"]');

              if (newCartPanel && oldCartPanel) {
                oldCartPanel.innerHTML = newCartPanel.innerHTML;
              }

              // Update cart count in header
              const cartCounts = document.querySelectorAll('cart-count');
              const newCartCount = newDrawer.querySelector('cart-count');
              if (newCartCount) {
                cartCounts.forEach(count => {
                  count.textContent = newCartCount.textContent;
                  count.hidden = newCartCount.hidden;
                });
              }

              // Update combined header badge
              this.updateHeaderBadge();
            }
          } catch (error) {
            console.error('QuoteList: Failed to refresh cart drawer', error);
          }
        }
      }
    }

    // ========== Move Operations ==========

    async moveToQuote(btn) {
      const variantId = btn.dataset.variantId;
      const lineKey = btn.dataset.lineKey;

      const productData = {
        id: variantId,
        productId: btn.dataset.productId,
        title: btn.dataset.title,
        variant: btn.dataset.variantTitle || '',
        isbn: btn.dataset.barcode || '',
        sku: btn.dataset.sku || '',
        author: btn.dataset.author || '',
        price: btn.dataset.price || '',
        image: btn.dataset.image || '',
        url: btn.dataset.url || ''
      };

      // Show loading state
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Moving...';

      try {
        // Add to quote list
        this.addItem(productData, { silent: true });

        // Remove from cart
        await this.removeFromCart(lineKey);

        // Refresh cart drawer
        await this.refreshCartDrawer();

        // Switch to quote tab
        this.switchToQuoteTab();

        this.showToast('Item moved to quote list', 'success');
      } catch (error) {
        console.error('QuoteList: Failed to move to quote', error);
        this.showToast('Failed to move item. Please try again.', 'error');
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }

    async moveToCart(variantId) {
      const item = this.getItem(variantId);
      if (!item) return;

      // Find the button and show loading state
      const btn = this.itemsList?.querySelector(`[data-move-to-cart="${variantId}"]`);
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Moving...';
      }

      try {
        // Add to cart
        await this.addToCart(variantId, 1);

        // Remove from quote list
        this.removeItem(variantId, { silent: true });

        // Refresh cart drawer
        await this.refreshCartDrawer();

        // Switch to cart tab
        this.switchToCartTab();

        this.showToast('Item moved to cart', 'success');
      } catch (error) {
        console.error('QuoteList: Failed to move to cart', error);
        this.showToast('Failed to move item. Please try again.', 'error');
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Move to Cart';
        }
      }
    }

    // ========== Cart Drawer Integration ==========

    openQuoteTab() {
      if (!this.cartDrawer) return;

      // Open the cart drawer if it has an open method
      if (this.cartDrawer.open) {
        this.cartDrawer.open();
      } else {
        // Fallback: trigger click on the cart icon to open drawer
        const cartIcon = document.querySelector('[aria-controls="CartDrawer"]');
        if (cartIcon && !this.cartDrawer.hasAttribute('active')) {
          cartIcon.click();
        }
      }

      // Switch to the Quote List tab
      setTimeout(() => {
        this.switchToQuoteTab();
      }, 100);
    }

    switchToQuoteTab() {
      // Find the Quote List tab button and click it
      const quoteTabBtn = this.cartDrawer?.querySelector('[aria-controls^="QuoteList-"]');
      if (quoteTabBtn && quoteTabBtn.getAttribute('aria-expanded') !== 'true') {
        quoteTabBtn.click();
      }
      // Ensure we're on Step 1 when opening the quote tab
      this.goToStep1();
    }

    switchToCartTab() {
      // Find the Cart tab button and click it
      const cartTabBtn = this.cartDrawer?.querySelector('[aria-controls^="MiniCart-"]');
      if (cartTabBtn && cartTabBtn.getAttribute('aria-expanded') !== 'true') {
        cartTabBtn.click();
      }
    }

    // ========== Content Updates ==========

    updateContent() {
      const count = this.getItemCount();
      const hasItems = count > 0;

      // Toggle empty state vs items
      if (this.emptyState) {
        this.emptyState.hidden = hasItems;
      }
      // Hide success message when items are added
      if (hasItems) {
        this.hideQuoteSuccessInDrawer();
      }
      // Items container visibility is handled by step navigation
      // Don't override it here - let goToStep1/goToStep2 handle it
      if (this.footer) {
        this.footer.hidden = !hasItems;
      }
      // Ensure we're on Step 1 when items are shown
      if (hasItems && this.currentStep === 2) {
        this.goToStep1();
      }

      // Update item count in footer
      if (this.itemCount) {
        this.itemCount.textContent = count;
      }

      // Update quote tab badge count
      const quoteBadges = document.querySelectorAll('[data-quote-count]');
      quoteBadges.forEach(badge => {
        badge.textContent = count;
        badge.hidden = count === 0;
      });

      // Update combined header badge
      this.updateHeaderBadge();

      // Render items
      if (this.itemsList) {
        this.renderItems();
      }
    }

    updateHeaderBadge() {
      // Get cart count from the cart-count element
      const cartCountEl = document.querySelector('cart-count');
      const cartCount = cartCountEl ? parseInt(cartCountEl.textContent) || 0 : 0;
      const quoteCount = this.getItemCount();
      const totalCount = cartCount + quoteCount;

      // Update header cart badges to show combined count
      const headerBadges = document.querySelectorAll('.header cart-count, header cart-count');
      headerBadges.forEach(badge => {
        badge.textContent = totalCount;
        badge.hidden = totalCount === 0;
      });
    }

    renderItems() {
      if (this.items.length === 0) {
        this.itemsList.innerHTML = '';
        return;
      }

      // Use the same horizontal-product structure as cart drawer
      const itemsHtml = this.items.map((item) => `
        <li class="horizontal-product flex items-start gap-4 md:gap-6" data-variant-id="${item.id}">
          ${item.image ? `
            <a class="horizontal-product__media media media--portrait relative overflow-hidden shrink-0" href="${item.url}" tabindex="-1">
              <img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="180" height="240">
            </a>
          ` : ''}
          <div class="horizontal-product__details grow flex flex-col justify-start align-self-center gap-1d5">
            <div class="flex flex-col gap-1">
              <div class="block leading-tight">
                <a href="${item.url}" class="horizontal-product__title reversed-link font-medium text-base leading-tight">${this.escapeHtml(item.title)}</a>
              </div>
              ${item.author ? `
                <div class="text-xs text-opacity leading-tight">${this.escapeHtml(item.author)}</div>
              ` : ''}
              ${item.isbn ? `
                <div class="text-xs text-opacity leading-tight">ISBN: ${this.escapeHtml(item.isbn)}</div>
              ` : ''}
            </div>
            ${item.price ? `
              <div class="price text-sm flex flex-wrap gap-1d5">
                ${item.price}
              </div>
            ` : ''}
          </div>
          <div class="horizontal-product__quantity align-self-stretch shrink-0 text-sm sm:block">
            <quantity-input class="cart-quantity flex items-center relative mb-2">
              <label class="sr-only" for="QuoteQty-${item.id}">Quantity</label>
              <input class="quantity__input input text-center w-full h-full"
                type="number"
                id="QuoteQty-${item.id}"
                data-quote-quantity="${item.id}"
                inputmode="numeric"
                autocomplete="off"
                min="1"
                step="1"
                value="${item.quantity || 1}"
              />
              <div class="quantity__buttons absolute right-0 hidden lg:flex flex-col items-center justify-center h-full">
                <button type="button" name="plus" class="quantity__button flex justify-center w-full items-end" aria-label="Increase quantity">
                  <svg class="icon icon-increase icon-sm stroke-1" viewBox="0 0 8 8" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M1 5L4 2L7 5"></path>
                  </svg>
                </button>
                <button type="button" name="minus" class="quantity__button flex justify-center w-full items-start" aria-label="Decrease quantity">
                  <svg class="icon icon-decrease icon-sm stroke-1" viewBox="0 0 8 8" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M1 3L4 6L7 3"></path>
                  </svg>
                </button>
              </div>
            </quantity-input>
            <div class="flex items-center justify-center">
              <button type="button" class="text-xs underline hover:no-underline" data-move-to-cart="${item.id}" title="Move to Cart" aria-label="Move to Cart">
                Move to Cart
              </button>
            </div>
          </div>
        </li>
      `).join('');

      this.itemsList.innerHTML = itemsHtml;

      // Add remove button listeners
      this.itemsList.querySelectorAll('[data-remove-quote]').forEach(btn => {
        btn.addEventListener('click', () => {
          const variantId = btn.dataset.removeQuote;
          this.removeItem(variantId);
        });
      });

      // Add move to cart button listeners
      this.itemsList.querySelectorAll('[data-move-to-cart]').forEach(btn => {
        btn.addEventListener('click', () => {
          const variantId = btn.dataset.moveToCart;
          this.moveToCart(variantId);
        });
      });

      // Add quantity change listeners
      this.itemsList.querySelectorAll('[data-quote-quantity]').forEach(input => {
        input.addEventListener('change', (e) => {
          const variantId = input.dataset.quoteQuantity;
          const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
          this.updateQuantity(variantId, newQuantity);
        });
      });

    }

    updateQuantity(variantId, quantity) {
      const index = this.items.findIndex(item => item.id === variantId);
      if (index >= 0) {
        this.items[index].quantity = quantity;
        this.save();
      }
    }

    // ========== Form Integration ==========

    generateQuoteMessage() {
      if (this.items.length === 0) return '';

      // Get all form field values
      const contactName = this.nameField?.value.trim() || '';
      const email = this.emailField?.value.trim() || '';
      const district = this.districtField?.value.trim() || '';
      const schoolName = this.schoolNameField?.value.trim() || '';
      const bidNumber = this.bidNumberField?.value.trim() || '';
      const streetAddress = this.streetAddressField?.value.trim() || '';
      const city = this.cityField?.value.trim() || '';
      const state = this.stateField?.value || '';
      const zipCode = this.zipCodeField?.value.trim() || '';
      const notes = this.notesField?.value.trim() || '';
      const phone = this.phoneField?.value.trim() || '';

      // Build message in the format matching the email template
      let message = 'Quote Request Details\n';
      message += '─'.repeat(40) + '\n';

      // Two-column format: Field Name | Value
      const formatField = (label, value, required = false) => {
        // Always show required fields, show optional fields only if they have values
        if (!required && !value) return '';
        const padding = ' '.repeat(Math.max(1, 30 - label.length));
        return `${label}${padding}| ${value || ''}\n`;
      };

      // Required fields (always show)
      message += formatField('Contact Name', contactName, true);
      message += formatField('Your Email', email, true);
      message += formatField('District', district, true);
      message += formatField('School Name & Department', schoolName, true);

      // Optional fields (show only if they have values)
      message += formatField('Bid Number', bidNumber);
      message += formatField('Street Address', streetAddress);
      message += formatField('City', city);
      message += formatField('State', state);
      message += formatField('Zip Code', zipCode);
      message += formatField('Phone', phone);
      message += formatField('Order Notes', notes);

      // Add uploaded files info if present
      if (this.uploadedFiles.length > 0) {
        const fileNames = this.uploadedFiles.map(file => `${file.name} (${this.formatFileSize(file.size)})`).join(', ');
        message += formatField('Attached Files', fileNames);
      }

      // Add tax exempt info if applicable
      const isTaxExempt = this.taxExemptCheckbox?.checked;
      if (isTaxExempt) {
        let taxExemptInfo = 'Yes';
        const taxExemptId = this.taxExemptIdField?.value.trim();
        const taxExemptDetails = this.taxExemptDetailsField?.value.trim();
        if (taxExemptId) {
          taxExemptInfo += ` (ID: ${taxExemptId})`;
        }
        if (taxExemptDetails) {
          taxExemptInfo += `\nDetails: ${taxExemptDetails}`;
        }
        message += formatField('Tax Exempt', taxExemptInfo);
      }

      message += '\n';
      message += 'Your Quote List\n';
      message += '─'.repeat(40) + '\n';

      // List book items
      this.items.forEach((item, index) => {
        message += `${index + 1}. ${item.title}\n`;
        if (item.isbn) message += `   ISBN: ${item.isbn}\n`;
        if (item.quantity && item.quantity > 1) message += `   Quantity: ${item.quantity}\n`;
        if (item.author) message += `   Author: ${item.author}\n`;
        if (item.price) message += `   Price: ${item.price}\n`;
        message += '\n';
      });

      // Calculate total quantity
      const totalQuantity = this.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      message += '─'.repeat(40) + '\n';
      message += `Total Items: ${this.items.length} (${totalQuantity} units)\n`;

      return message;
    }

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    validateQuoteForm() {
      const name = this.nameField?.value.trim();
      const email = this.emailField?.value.trim();
      const district = this.districtField?.value.trim();
      const schoolName = this.schoolNameField?.value.trim();

      if (!name) {
        this.showToast('Please enter your contact name.', 'error');
        if (this.nameField) {
          this.nameField.focus();
          this.nameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }

      if (!email) {
        this.showToast('Please enter your email address.', 'error');
        if (this.emailField) {
          this.emailField.focus();
          this.emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.showToast('Please enter a valid email address.', 'error');
        if (this.emailField) {
          this.emailField.focus();
          this.emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }

      if (!district) {
        this.showToast('Please enter your district.', 'error');
        if (this.districtField) {
          this.districtField.focus();
          this.districtField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }

      if (!schoolName) {
        this.showToast('Please enter your school name & department.', 'error');
        if (this.schoolNameField) {
          this.schoolNameField.focus();
          this.schoolNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }

      return true;
    }

    async handleQuoteSubmit() {
      if (this.items.length === 0) {
        this.showToast('Please add at least one item to your quote list.', 'error');
        return;
      }

      // Validate required fields
      if (!this.validateQuoteForm()) {
        return;
      }

      // Make sure we have the form element
      if (!this.form) {
        console.error('QuoteList: Contact form not found');
        this.showToast('Form configuration error. Please refresh and try again.', 'error');
        return;
      }

      // Update hidden form fields
      if (this.hiddenName) this.hiddenName.value = this.nameField?.value || '';
      if (this.hiddenEmail) this.hiddenEmail.value = this.emailField?.value || '';
      if (this.hiddenPhone) this.hiddenPhone.value = this.phoneField?.value || '';
      if (this.hiddenBody) this.hiddenBody.value = this.generateQuoteMessage();

      // Find submit button
      const submitBtn = document.querySelector('[data-quote-submit]');
      if (submitBtn) {
        submitBtn.disabled = true;
        const btnText = submitBtn.querySelector('.btn-text');
        if (btnText) {
          btnText.dataset.originalText = btnText.innerHTML;
          btnText.innerHTML = 'Sending...';
        }
      }

      try {
        // Use native form submission to handle Shopify's CAPTCHA requirement
        // Create a hidden iframe to capture the form response
        const iframeName = 'quote-submit-frame-' + Date.now();
        const iframe = document.createElement('iframe');
        iframe.name = iframeName;
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Set form target to iframe
        this.form.target = iframeName;

        // Listen for iframe load (form submission complete)
        const submitPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Form submission timeout'));
          }, 30000);

          iframe.onload = () => {
            clearTimeout(timeout);
            try {
              // Check if we can access the iframe content (same-origin)
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              const bodyText = iframeDoc.body ? iframeDoc.body.innerText : '';

              // Shopify redirects to a thank you page or shows success message
              // Check for common error patterns
              if (bodyText.toLowerCase().includes('error') && bodyText.toLowerCase().includes('captcha')) {
                reject(new Error('CAPTCHA verification failed'));
              } else {
                resolve();
              }
            } catch (e) {
              // Cross-origin - assume success if no error thrown during submit
              resolve();
            }
          };

          iframe.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Form submission failed'));
          };
        });

        // Submit the form natively
        this.form.submit();

        // Wait for completion
        await submitPromise;

        // Clean up
        setTimeout(() => {
          iframe.remove();
        }, 1000);

        // Send confirmation email with CC to requester
        try {
          await this.sendConfirmationEmail();
        } catch (emailError) {
          // Email failure should not block success message, but log it
          console.error('Failed to send confirmation email:', emailError);
          // Don't throw - form submission succeeded even if email fails
          // User will still see success message for form submission
        }

        // Clear items and reset form, then show success on empty state
        this.clearAll();
        this.resetQuoteForm();
        this.showQuoteSuccessInDrawer();
      } catch (error) {
        console.error('Quote form submission error:', error);
        // Hide success message if it was shown
        this.hideQuoteSuccessInDrawer();
        // Restore button
        if (submitBtn) {
          const btnText = submitBtn.querySelector('.btn-text');
          if (btnText && btnText.dataset.originalText) {
            btnText.innerHTML = btnText.dataset.originalText;
          }
        }
        // Show error message
        this.showToast('Failed to send quote request. Please try again.', 'error');
      } finally {
        // Button will be changed to Close on success, so only handle failure case here
        // Success case is handled in changeSubmitButtonToClose()
        if (submitBtn && !submitBtn.dataset.quoteClose) {
          submitBtn.disabled = false;
          const btnText = submitBtn.querySelector('.btn-text');
          if (btnText && btnText.dataset.originalText) {
            btnText.innerHTML = btnText.dataset.originalText;
          }
        }
      }
    }

    /**
     * Send confirmation email with CC to requester
     */
    /**
     * Convert File objects to base64 for API transmission
     */
    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:type;base64, prefix
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            data: base64
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    async sendConfirmationEmail() {
      // Get API URL from data attribute or use default
      const apiUrl = document.body.dataset.apiUrl || 'https://qep-isbn-lookup.onrender.com';
      
      // Collect form data
      const name = this.nameField?.value || this.hiddenName?.value || '';
      const email = this.emailField?.value || this.hiddenEmail?.value || '';
      const phone = this.phoneField?.value || this.hiddenPhone?.value || '';
      const message = this.hiddenBody?.value || '';
      const district = this.districtField?.value || '';
      const schoolName = this.schoolNameField?.value || '';
      const bidNumber = this.bidNumberField?.value || '';
      const streetAddress = this.streetAddressField?.value || '';
      const city = this.cityField?.value || '';
      const state = this.stateField?.value || '';
      const zipCode = this.zipCodeField?.value || '';
      const notes = this.notesField?.value || '';
      const isTaxExempt = this.taxExemptCheckbox?.checked || false;
      const taxExemptId = this.taxExemptIdField?.value || '';
      const taxExemptDetails = this.taxExemptDetailsField?.value || '';
      
      // Convert uploaded files to base64
      const uploadedFiles = [];
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        for (const file of this.uploadedFiles) {
          try {
            const fileData = await this.fileToBase64(file);
            uploadedFiles.push(fileData);
          } catch (error) {
            console.error('Failed to convert file to base64:', file.name, error);
          }
        }
      }
      
      // Prepare quote items with prices if available
      const quoteItems = this.items.map(item => ({
        title: item.title || item.product_title || 'N/A',
        isbn: item.isbn || item.variant_barcode || 'N/A',
        quantity: item.quantity || 1,
        price: item.price || item.final_price || null,
        author: item.author || null
      }));

      // Send to API endpoint
      console.log('📧 Sending confirmation email request to:', `${apiUrl}/api/quote-request/confirm`);
      
      const response = await fetch(`${apiUrl}/api/quote-request/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          district,
          schoolName,
          bidNumber,
          streetAddress,
          city,
          state,
          zipCode,
          notes,
          isTaxExempt,
          taxExemptId,
          taxExemptDetails,
          uploadedFiles,
          quoteItems
        })
      }).catch((networkError) => {
        // Network errors (connection refused, timeout, etc.)
        console.error('Network error sending confirmation email:', networkError);
        throw new Error(`Failed to connect to email service: ${networkError.message}`);
      });

      const responseData = await response.json().catch(() => ({ error: 'Failed to parse response' }));

      if (!response.ok) {
        console.error('❌ API Error:', responseData);
        throw new Error(responseData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('✅ Confirmation email sent successfully:', responseData);
      return responseData;
    }

    resetQuoteForm() {
      // Reset all form fields
      if (this.notesField) this.notesField.value = '';
      if (this.nameField) this.nameField.value = '';
      if (this.emailField) this.emailField.value = '';
      if (this.phoneField) this.phoneField.value = '';
      if (this.uploadField) this.uploadField.value = '';
      if (this.uploadPreview) this.uploadPreview.innerHTML = '';
      this.uploadedFiles = [];

      // Reset new Step 2 fields
      if (this.districtField) this.districtField.value = '';
      if (this.schoolNameField) this.schoolNameField.value = '';
      if (this.bidNumberField) this.bidNumberField.value = '';
      if (this.streetAddressField) this.streetAddressField.value = '';
      if (this.cityField) this.cityField.value = '';
      if (this.stateField) this.stateField.value = '';
      if (this.zipCodeField) this.zipCodeField.value = '';

      // Reset tax exempt fields
      if (this.taxExemptCheckbox) this.taxExemptCheckbox.checked = false;
      if (this.taxExemptIdField) this.taxExemptIdField.value = '';
      if (this.taxExemptDetailsField) this.taxExemptDetailsField.value = '';

      // Reset hidden form fields
      if (this.hiddenName) this.hiddenName.value = '';
      if (this.hiddenEmail) this.hiddenEmail.value = '';
      if (this.hiddenPhone) this.hiddenPhone.value = '';
      if (this.hiddenBody) this.hiddenBody.value = '';

      // Return to Step 1
      this.goToStep1();
    }

    handleFileUpload(e) {
      const newFiles = Array.from(e.target.files);

      if (newFiles.length === 0) {
        return;
      }

      // Append new files to existing list (avoid duplicates by name)
      newFiles.forEach(newFile => {
        const exists = this.uploadedFiles.some(f => f.name === newFile.name && f.size === newFile.size);
        if (!exists) {
          this.uploadedFiles.push(newFile);
        }
      });

      this.updateUploadPreview();
    }

    updateUploadPreview() {
      if (!this.uploadPreview) return;

      if (this.uploadedFiles.length === 0) {
        this.uploadPreview.innerHTML = '';
        return;
      }

      const previewHtml = this.uploadedFiles.map((file, index) => `
        <div class="flex items-center justify-between gap-2 text-sm" data-file-index="${index}">
          <span class="truncate">${this.escapeHtml(file.name)}</span>
          <div class="flex items-center gap-2 shrink-0">
            <span class="text-subdued">${this.formatFileSize(file.size)}</span>
            <button type="button" class="flex items-center justify-center text-subdued hover:text-current" data-remove-file="${index}" aria-label="Remove file">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      `).join('');

      this.uploadPreview.innerHTML = previewHtml;

      // Add remove file listeners
      this.uploadPreview.querySelectorAll('[data-remove-file]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const fileIndex = parseInt(btn.dataset.removeFile);
          this.removeFile(fileIndex);
        });
      });
    }

    removeFile(index) {
      if (index >= 0 && index < this.uploadedFiles.length) {
        this.uploadedFiles.splice(index, 1);
        this.updateUploadPreview();
      }
    }

    // ========== Success Message in Drawer ==========

    showQuoteSuccessInDrawer() {
      if (this.successMessage) {
        this.successMessage.hidden = false;
        this.successMessage.classList.remove('hidden');
      }
    }

    hideQuoteSuccessInDrawer() {
      if (this.successMessage) {
        this.successMessage.hidden = true;
        this.successMessage.classList.add('hidden');
      }
    }

    changeSubmitButtonToClose() {
      const submitBtn = document.querySelector('[data-quote-submit]');
      if (!submitBtn) return;
      
      // Change button attributes
      submitBtn.dataset.quoteClose = 'true';
      submitBtn.removeAttribute('data-quote-submit');
      submitBtn.disabled = false; // Re-enable button
      
      const btnText = submitBtn.querySelector('.btn-text');
      if (btnText) {
        // Create close icon SVG (matching theme icon style)
        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('width', '16');
        iconSvg.setAttribute('height', '16');
        iconSvg.setAttribute('viewBox', '0 0 24 24');
        iconSvg.setAttribute('fill', 'none');
        iconSvg.setAttribute('stroke', 'currentColor');
        iconSvg.setAttribute('stroke-width', '2');
        iconSvg.setAttribute('stroke-linecap', 'round');
        iconSvg.setAttribute('stroke-linejoin', 'round');
        iconSvg.innerHTML = '<path d="M18 6L6 18M6 6l12 12"></path>';
        iconSvg.className = 'hidden sm:block';
        iconSvg.style.width = '1em';
        iconSvg.style.height = '1em';
        
        // Update button text
        btnText.innerHTML = '';
        btnText.appendChild(iconSvg);
        btnText.appendChild(document.createTextNode(' Close'));
        
        // Set click handler to close drawer
        submitBtn.onclick = (e) => {
          e.preventDefault();
          this.closeQuoteDrawerAfterSuccess();
        };
      }
    }

    restoreSubmitButton() {
      const submitBtn = document.querySelector('[data-quote-close]');
      if (!submitBtn) return;
      
      // Restore button to Submit
      submitBtn.dataset.quoteSubmit = 'true';
      submitBtn.removeAttribute('data-quote-close');
      submitBtn.onclick = null; // Remove custom handler
      
      const btnText = submitBtn.querySelector('.btn-text');
      if (btnText) {
        // Restore original Submit Quote button text
        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('width', '16');
        iconSvg.setAttribute('height', '16');
        iconSvg.setAttribute('viewBox', '0 0 24 24');
        iconSvg.setAttribute('fill', 'none');
        iconSvg.setAttribute('stroke', 'currentColor');
        iconSvg.setAttribute('stroke-width', '2');
        iconSvg.innerHTML = '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline>';
        iconSvg.className = 'hidden sm:block';
        iconSvg.style.width = '1em';
        iconSvg.style.height = '1em';
        
        btnText.innerHTML = '';
        btnText.appendChild(iconSvg);
        btnText.appendChild(document.createTextNode(' Submit Quote'));
      }
    }

    closeQuoteDrawerAfterSuccess() {
      // Clear quote list and reset form
      this.clearAll();
      this.resetQuoteForm();
      
      // Hide success message
      this.hideQuoteSuccessInDrawer();
      
      // Restore button
      this.restoreSubmitButton();
      
      // Close the drawer by clicking the close button
      const closeBtn = this.cartDrawer?.querySelector('.drawer__close');
      if (closeBtn) {
        closeBtn.click();
      } else {
        // Fallback: directly hide the drawer
        const cartDrawer = document.getElementById('CartDrawer');
        if (cartDrawer) {
          cartDrawer.hidden = true;
          const overlay = document.querySelector('[aria-controls="CartDrawer"]');
          if (overlay) {
            overlay.setAttribute('aria-expanded', 'false');
          }
        }
      }
    }

    showToast(message, type = 'success') {
      // Remove existing toast
      const existingToast = document.querySelector('.quote-toast');
      if (existingToast) existingToast.remove();

      // Create toast
      const toast = document.createElement('div');
      toast.className = `quote-toast quote-toast--${type}`;
      toast.innerHTML = `
        <span class="quote-toast__message">${message}</span>
        <button type="button" class="quote-toast__close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </button>
      `;

      document.body.appendChild(toast);

      // Trigger animation
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });

      // Close button
      toast.querySelector('.quote-toast__close').addEventListener('click', () => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 300);
      });

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        if (toast.parentNode) {
          toast.classList.remove('is-visible');
          setTimeout(() => toast.remove(), 300);
        }
      }, 5000);
    }

    // ========== Utilities ==========

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    // ========== Step Navigation ==========

    goToStep1() {
      this.currentStep = 1;
      // Hide Step 2 first
      if (this.step2) {
        this.step2.hidden = true;
      }
      // Show Step 1
      if (this.step1) {
        this.step1.hidden = false;
      }
      // Show items list (it's inside Step 1)
      if (this.itemsContainer) {
        this.itemsContainer.hidden = false;
      }
      // Scroll to top of step 1
      if (this.itemsContainer) {
        this.itemsContainer.scrollTop = 0;
      }
    }

    goToStep2() {
      if (this.items.length === 0) {
        this.showToast('Please add at least one item to your quote list.', 'error');
        return;
      }
      this.currentStep = 2;
      // Hide Step 1 (which contains items list)
      if (this.step1) {
        this.step1.hidden = true;
      }
      // Show Step 2 (form)
      if (this.step2) {
        this.step2.hidden = false;
      }
      // Items list is inside Step 1, so it will be hidden when Step 1 is hidden
      // Scroll to top of step 2
      const scrollable = this.step2?.querySelector('.drawer__scrollable');
      if (scrollable) {
        scrollable.scrollTop = 0;
      }
    }

    // ========== Initialization ==========

    init() {
      // Ensure we start on Step 1 before updating content
      this.goToStep1();
      
      // Update content on load
      this.updateContent();

      // Clear all button
      document.addEventListener('click', (e) => {
        const clearBtn = e.target.closest('[data-quote-clear]');
        if (clearBtn) {
          e.preventDefault();
          if (confirm('Clear all items from your quote list?')) {
            this.clearAll();
            this.resetQuoteForm();
            this.goToStep1();
          }
        }
      });

      // Next button (Step 1 -> Step 2)
      document.addEventListener('click', (e) => {
        const nextBtn = e.target.closest('[data-quote-next]');
        if (nextBtn) {
          e.preventDefault();
          this.goToStep2();
        }
      });

      // Back button (Step 2 -> Step 1)
      document.addEventListener('click', (e) => {
        const backBtn = e.target.closest('[data-quote-back]');
        if (backBtn) {
          e.preventDefault();
          this.goToStep1();
        }
      });

      // Submit button (Step 2)
      document.addEventListener('click', (e) => {
        const submitBtn = e.target.closest('[data-quote-submit]');
        if (submitBtn) {
          e.preventDefault();
          this.handleQuoteSubmit();
        }
      });

      // Close button after success (handler is set in changeSubmitButtonToClose)

      // File upload handling
      if (this.uploadField) {
        this.uploadField.addEventListener('change', (e) => this.handleFileUpload(e));
      }


      // Drag & drop handling for upload zones (Step 2)
      const uploadZones = document.querySelectorAll('[data-upload-zone]');
      uploadZones.forEach(uploadZone => {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(event => {
          uploadZone.addEventListener(event, (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
        });

        ['dragenter', 'dragover'].forEach(event => {
          uploadZone.addEventListener(event, () => {
            uploadZone.classList.add('border-current', 'bg-opacity-5');
          });
        });

        ['dragleave', 'drop'].forEach(event => {
          uploadZone.addEventListener(event, () => {
            uploadZone.classList.remove('border-current', 'bg-opacity-5');
          });
        });

        uploadZone.addEventListener('drop', (e) => {
          const files = e.dataTransfer.files;
          if (files.length > 0) {
            // Handle dropped files directly (append to existing)
            this.handleFileUpload({ target: { files } });
          }
        });
      });

      // Add to quote buttons (delegate to document for dynamic buttons)
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-quote-add]');
        if (addBtn) {
          e.preventDefault();

          const productData = {
            id: addBtn.dataset.variantId,
            productId: addBtn.dataset.productId,
            title: addBtn.dataset.title,
            variant: addBtn.dataset.variant || '',
            isbn: addBtn.dataset.isbn || '',
            sku: addBtn.dataset.sku || '',
            author: addBtn.dataset.author || '',
            price: addBtn.dataset.price || '',
            image: addBtn.dataset.image || '',
            url: addBtn.dataset.url || ''
          };

          this.addItem(productData);
        }
      });

      // Move to quote buttons (in cart items)
      document.addEventListener('click', (e) => {
        const moveBtn = e.target.closest('[data-move-to-quote]');
        if (moveBtn) {
          e.preventDefault();
          this.moveToQuote(moveBtn);
        }
      });

      // Quote button in header or elsewhere to open quote tab
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-quote-open]');
        if (openBtn) {
          e.preventDefault();
          this.openQuoteTab();
        }
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.quoteList = new QuoteList();
    });
  } else {
    window.quoteList = new QuoteList();
  }

})();
